import { BoardNameFamily, boardNameFamily } from './boardNameFamily'
import type { Device } from '@/types/devices.api'

export type TrackerProduct = 'classic' | 'mini' | 'bike' | 'industrial' | 'pro' | 'smartDogCollar' | 'proMini' | 'biotracker' | 'biotrackerV2' | 'proMax'

const petUsages = ['dog', 'cat', 'pet']

const labels: Record<TrackerProduct, string> = {
  classic: 'GPS Tracker Classic',
  mini: 'Mini Tracker',
  bike: 'Bike Tracker',
  industrial: 'Industrial Tracker',
  pro: 'GPS Tracker Pro',
  smartDogCollar: 'Smart Dog Collar',
  proMini: 'GPS Tracker Pro Mini',
  biotracker: 'Biotracker',
  biotrackerV2: 'Biotracker',
  proMax: 'GPS Tracker Pro Max',
}

const images: Record<TrackerProduct, string> = {
  classic: '/images/trackers/lwt1.webp',
  mini: '/images/trackers/lwt2-mini.webp',
  bike: '/images/trackers/lwt2-bike.webp',
  industrial: '/images/trackers/sct2.webp',
  pro: '/images/trackers/lwt3.webp',
  smartDogCollar: '/images/trackers/smart-dog-collar.webp',
  proMini: '/images/trackers/lwt6.webp',
  biotracker: '/images/trackers/lwt6m1.webp',
  biotrackerV2: '/images/trackers/lwt6.webp',
  proMax: '/images/trackers/lwt7.webp',
}

// Monochrome line-icon (in public/images/portal-icons) used to head a product
// group in the Abonnements list. Maps each product to the closest universe icon.
const icons: Record<TrackerProduct, string> = {
  classic: 'map-pin-area',
  mini: 'map-pin-area',
  bike: 'map-pin-area',
  industrial: 'cell-tower',
  pro: 'cell-tower',
  smartDogCollar: 'heart-rate',
  proMini: 'cell-tower',
  biotracker: 'heart-rate',
  biotrackerV2: 'heart-rate',
  proMax: 'cell-tower',
}

export const trackerProductLabel = (product: TrackerProduct | null): string => (product ? labels[product] : 'Tracker')

export const trackerProductImage = (product: TrackerProduct | null): string => (product ? images[product] : '/images/trackers/lwt1.webp')

export const trackerProductIcon = (product: TrackerProduct | null): string => (product ? icons[product] : 'tag')

// Resolve from the board family alone (e.g. webview flows that only receive a board_name query param). usage refines the families sold as several products.
export const trackerProductForFamily = (family: BoardNameFamily | null, usage?: string | null): TrackerProduct | null => {
  switch (family) {
    case 'LWT1':
      return 'classic'
    case 'LWT2':
      return usage === 'bike' ? 'bike' : 'mini'
    case 'LWT3':
      return 'pro'
    case 'SCT2':
      return 'industrial'
    case 'LWT6':
      return 'proMini'
    case 'LWT6M1':
      return 'biotracker'
    case 'LWT6V2':
      return 'biotrackerV2'
    case 'LWT7':
      return 'proMax'
    default:
      // unknown board, or LWT4 which was never commercialized
      return null
  }
}

// The pet biotracker line — the products whose subscription tiers (and trial
// wording) are surfaced to the user, as opposed to plain GPS trackers.
export const isBiotrackerDevice = (device: Device | null | undefined): boolean => {
  const product = trackerProductForDevice(device)
  return product === 'biotracker' || product === 'biotrackerV2'
}

// Resolve from a full device: the board name wins when present, the backend
// device type fills in when it is not synced yet.
export const trackerProductForDevice = (device: Device | null | undefined): TrackerProduct | null => {
  if (!device?.type) return null

  if (device.type === 'tracker_04') return 'smartDogCollar'

  const usage = device.tracker_config?.usage
  const product = trackerProductForFamily(boardNameFamily(device.tracker_config?.board_name), usage)
  if (product) return product

  switch (device.type) {
    case 'tracker_01':
      return 'classic'
    case 'tracker_03':
      return 'pro'
    case 'tracker_06':
      // Biotracker boards report LWT6M1; without a board name, usage is the only hint left
      return usage && petUsages.includes(usage) ? 'biotracker' : 'proMini'
    case 'tracker_07':
      return 'proMax'
    default:
      return null
  }
}
